import { Box, Icon, Typography, useTheme } from "@outschool/backpack";
import * as FilestackUrls from "@outschool/filestack-urls";
import { Meeting } from "@outschool/gql-backend-generated";
import {
  ActivitySummary_ActivityFragment,
  ActivitySummary_SectionFragment,
} from "@outschool/gql-frontend-generated";
import { fasExclamationTriangle } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import * as Time from "@outschool/time";
import { gql } from "@outschool/ui-apollo";
import { useSelfPacedIconInfo } from "@outschool/ui-components-website";
import { Banner, Subscript } from "@outschool/ui-legacy-component-library";
import { Screen } from "@outschool/ui-utils";
import React from "react";

import useSectionString from "../../../shared/hooks/useSectionString";
import { useTimeZone } from "../../providers/TimeZoneProvider";
import { useOrderContext } from "./OrderContext";
import { EnrollmentOrgTeacher } from "./types";
import {
  VirginiaLAGAdjustmentAlert,
  useVirginiaSelfPacedAdjustment,
} from "./VirginiaLAGAdjustmentAlert";

type ActivitySummaryProps = {
  activity: ActivitySummary_ActivityFragment;
  section?: ActivitySummary_SectionFragment;
  startTime: Date;
  endTime: Date;
  nextOngoingMeeting?: Pick<Meeting, "start_time" | "end_time"> | null;
  numMeetings?: number | undefined;
  isRecurring?: boolean | undefined;
  orgTeacher?: EnrollmentOrgTeacher;
  meetings?: Pick<Meeting, "start_time" | "end_time">[];
};

function ActivitySummary({
  activity,
  section,
  meetings,
  startTime,
  endTime,
  nextOngoingMeeting,
  numMeetings,
  isRecurring,
  orgTeacher,
}: ActivitySummaryProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\ActivitySummary",
  );
  const theme = useTheme();
  const { ongoingStartTime } = useOrderContext();
  if (ongoingStartTime && meetings) {
    const meetingsAfterOngoingStart = meetings.filter(meeting =>
      Time.dayjs(meeting.start_time).isSameOrAfter(ongoingStartTime),
    );
    if (meetingsAfterOngoingStart.length > 0) {
      nextOngoingMeeting = meetingsAfterOngoingStart[0];
    }
  }
  const { fullDurationStringNotClub } = useSectionString();
  const { timeZone: userTimeZone } = useTimeZone();
  const resolutionMultiplier = Screen.useResolutionMultiplier();
  const { hasWeeksOfFeedbackAdjustment, feedbackTimeText } =
    useVirginiaSelfPacedAdjustment({
      activity,
      selfPacedStartTime: section?.start_time,
    });
  const selfPacedIconInfo = useSelfPacedIconInfo(activity);

  const isDuringNightTime = Time.isNightTime(startTime, userTimeZone);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "1em",
        }}
      >
        {!!activity.details.photo && (
          <Box
            sx={{
              backgroundImage:
                "url('" +
                FilestackUrls.imageUrl(activity.details.photo, {
                  h: 75 * resolutionMultiplier,
                  w: 75 * resolutionMultiplier,
                }) +
                "')",

              width: "5em",
              minWidth: "5em",
              height: "5em",
              borderRadius: "0.25em",
              backgroundSize: "cover",
              marginRight: "1em",
            }}
          />
        )}
        <Box
          sx={{
            flexGrow: 1,
          }}
        >
          <Box
            sx={{
              marginRight: "1em",
            }}
          >
            {activity.titleTranslated}
          </Box>
          {!activity.isClub && (
            <>
              {/* @ts-ignore TS(2532): Object is possibly 'undefined'. */}
              {numMeetings > 1 && (
                <Subscript
                  sx={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("{{numMeetings}} meetings starting on", { numMeetings })}
                </Subscript>
              )}
              {activity.isSelfPaced && (
                <Subscript>
                  {feedbackTimeText}, {selfPacedIconInfo.numberOfLessons.text},{" "}
                  {selfPacedIconInfo.accessToContent.text}
                </Subscript>
              )}
              {/* Recurring meeting requests for 1-on-1 */}
              {numMeetings === 1 && isRecurring ? (
                <>
                  <Subscript>
                    {t("Meet weekly: {{dayOfWeek}}s {{durationString}}", {
                      dayOfWeek: Time.formatFullWeekday(
                        startTime,
                        userTimeZone,
                      ),
                      durationString: Time.briefDurationTimesString(
                        startTime,
                        endTime,
                        userTimeZone,
                      ),
                    })}
                  </Subscript>
                  <Subscript>
                    {t("Start: {{startDate}} - End: When I stop", {
                      startDate: Time.startDayShortStringWithYear(
                        startTime,
                        userTimeZone,
                      ),
                    })}
                  </Subscript>
                </>
              ) : (
                !activity.isSelfPaced && (
                  <Subscript>
                    {isDuringNightTime &&
                      !activity.isClub &&
                      !activity.isFlexSchedule && (
                        <Icon
                          icon={fasExclamationTriangle}
                          sx={{
                            fontSize: "0.8em",
                            color: "warning.500",
                            marginRight: "0.5em",
                          }}
                        />
                      )}
                    {fullDurationStringNotClub({
                      activity,
                      section: { start_time: startTime, end_time: endTime },
                      nextOngoingMeeting,
                      userTimeZone,
                    })}
                  </Subscript>
                )
              )}
              {orgTeacher && (
                <Subscript>
                  {t("Teacher: {{teacherName}}", {
                    teacherName: orgTeacher.name,
                  })}
                </Subscript>
              )}
            </>
          )}
          {!!section?.allowsLateEnrollment && !!section?.progress && (
            <Box>
              <Typography variant="body2" emphasized>
                {t("Course in Progress: ")}
              </Typography>
              <Typography variant="body2">
                {t(
                  "{{pastMeetingCount}} of {{totalMeetingCount}} meetings has already occurred.",
                  {
                    pastMeetingCount: section.progress.pastMeetingCount,
                    totalMeetingCount: section.progress.totalMeetingCount,
                    count: section.progress.pastMeetingCount,
                    defaultValue_plural:
                      "{{pastMeetingCount}} of {{totalMeetingCount}} meetings have already occurred.",
                  },
                )}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      {isDuringNightTime && !activity.isClub && !activity.isFlexSchedule && (
        <Banner round>
          {t`This teacher may live in a different time zone. This class is outside \
6am-10pm in your local time.`}
        </Banner>
      )}
      {hasWeeksOfFeedbackAdjustment && (
        <VirginiaLAGAdjustmentAlert alertSx={{ marginBottom: "1em" }} />
      )}
    </>
  );
}

ActivitySummary.fragments = {
  activity: gql`
    fragment ActivitySummary_activity on Activity {
      uid
      details {
        photo
      }
      title
      titleTranslated
      weekly_meetings
      duration_weeks
      is_ongoing_weekly
      isClub
      isFlexSchedule
      isSelfPaced
      published_at
      hasTeacherSchedule
      size_max
    }
  `,
  section: gql`
    fragment ActivitySummary_section on Section {
      uid
      nextOngoingMeeting {
        uid
        start_time
      }
      start_time
      end_time
      allowsLateEnrollment
      progress {
        pastMeetingCount
        remainingMeetingCount
        totalMeetingCount
        nextMeeting {
          start_time
        }
      }
    }
  `,
};

export default ActivitySummary;
